import { useState, useEffect } from "react";
import { Users, Loader2, Search, Crown, Filter, MoreHorizontal, Pencil, Trash2, Plus, AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface UserProfile {
  id: string;
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
  email?: string;
  subscription?: {
    id?: string;
    plan_name: string;
    status: string;
  } | null;
}

const planColors: Record<string, string> = {
  "Base": "bg-zinc-500/20 text-zinc-300 border-zinc-500/30",
  "Plus": "bg-blue-500/20 text-blue-300 border-blue-500/30",
  "Pro": "bg-purple-500/20 text-purple-300 border-purple-500/30",
  "Ultimate": "bg-amber-500/20 text-amber-300 border-amber-500/30",
};

const PLAN_PRICES: Record<string, number> = {
  "Base": 0.99,
  "Plus": 1.99,
  "Pro": 2.99,
  "Ultimate": 3.99,
};

export const AdminUsersTable = () => {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [planFilter, setPlanFilter] = useState<string>("all");
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<string>("");
  const [saving, setSaving] = useState(false);
  
  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UserProfile | null>(null);
  const [deleting, setDeleting] = useState(false);
  
  // Delete account dialog state
  const [deleteAccountDialogOpen, setDeleteAccountDialogOpen] = useState(false);
  const [userToDeleteAccount, setUserToDeleteAccount] = useState<UserProfile | null>(null);
  const [deletingAccount, setDeletingAccount] = useState(false);

  useEffect(() => {
    fetchUsersWithSubscriptions();
  }, []);

  const fetchUsersWithSubscriptions = async () => {
    try {
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (profilesError) throw profilesError;

      const { data: subscriptions, error: subsError } = await supabase
        .from('subscriptions')
        .select('id, user_id, plan_name, status')
        .eq('status', 'active');

      if (subsError) throw subsError;

      const subsMap = new Map(
        subscriptions?.map(sub => [sub.user_id, { id: sub.id, plan_name: sub.plan_name, status: sub.status }])
      );

      const usersWithSubs: UserProfile[] = (profiles || []).map(profile => ({
        ...profile,
        subscription: subsMap.get(profile.user_id) || null
      }));

      setUsers(usersWithSubs);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Errore nel caricamento degli utenti');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubscription = (user: UserProfile) => {
    setSelectedUser(user);
    setSelectedPlan(user.subscription?.plan_name || "Base");
    setEditDialogOpen(true);
  };

  const handleSaveSubscription = async () => {
    if (!selectedUser) return;
    
    setSaving(true);
    try {
      if (selectedUser.subscription?.id) {
        // Update existing subscription
        const { error } = await supabase
          .from('subscriptions')
          .update({ 
            plan_name: selectedPlan,
            plan_price: PLAN_PRICES[selectedPlan],
            updated_at: new Date().toISOString()
          })
          .eq('id', selectedUser.subscription.id);

        if (error) throw error;
        toast.success(`Piano aggiornato a ${selectedPlan}`);
      } else {
        // Create new subscription
        const { error } = await supabase
          .from('subscriptions')
          .insert({
            user_id: selectedUser.user_id,
            plan_name: selectedPlan,
            plan_price: PLAN_PRICES[selectedPlan],
            status: 'active'
          });

        if (error) throw error;
        toast.success(`Abbonamento ${selectedPlan} creato`);
      }
      
      setEditDialogOpen(false);
      fetchUsersWithSubscriptions();
    } catch (error) {
      console.error('Error saving subscription:', error);
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSubscription = (user: UserProfile) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteSubscription = async () => {
    if (!userToDelete?.subscription?.id) return;
    
    setDeleting(true);
    try {
      const { error } = await supabase
        .from('subscriptions')
        .delete()
        .eq('id', userToDelete.subscription.id);

      if (error) throw error;
      
      toast.success('Abbonamento rimosso');
      setDeleteDialogOpen(false);
      fetchUsersWithSubscriptions();
    } catch (error) {
      console.error('Error deleting subscription:', error);
      toast.error('Errore nella rimozione');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteAccount = (user: UserProfile) => {
    setUserToDeleteAccount(user);
    setDeleteAccountDialogOpen(true);
  };

  const confirmDeleteAccount = async () => {
    if (!userToDeleteAccount) return;
    
    setDeletingAccount(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Non autenticato");

      const { data, error } = await supabase.functions.invoke('delete-account', {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: { target_user_id: userToDeleteAccount.user_id },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast.success('Account eliminato');
      setDeleteAccountDialogOpen(false);
      fetchUsersWithSubscriptions();
    } catch (error: any) {
      console.error('Error deleting account:', error);
      toast.error(error.message || 'Errore eliminazione account');
    } finally {
      setDeletingAccount(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const searchLower = searchQuery.toLowerCase();
    const fullName = `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase();
    const matchesSearch = (
      fullName.includes(searchLower) ||
      user.user_id.toLowerCase().includes(searchLower) ||
      (user.subscription?.plan_name?.toLowerCase().includes(searchLower))
    );
    
    if (planFilter === "all") return matchesSearch;
    if (planFilter === "none") return matchesSearch && !user.subscription;
    return matchesSearch && user.subscription?.plan_name === planFilter;
  });

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Search & Filter */}
      <div className="p-4 border-b border-border/50 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Cerca utente..."
            className="pl-10"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <Select value={planFilter} onValueChange={setPlanFilter}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Filtra per piano" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i piani</SelectItem>
              <SelectItem value="none">Senza abbonamento</SelectItem>
              <SelectItem value="Base">Base</SelectItem>
              <SelectItem value="Plus">Plus</SelectItem>
              <SelectItem value="Pro">Pro</SelectItem>
              <SelectItem value="Ultimate">Ultimate</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Users Table */}
      <div className="flex-1 overflow-y-auto p-4">
        {filteredUsers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Users className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground font-medium">
              {searchQuery ? "Nessun utente trovato" : "Nessun utente"}
            </p>
          </div>
        ) : (
          <div className="rounded-lg border border-border/50 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <TableHead className="font-semibold">Utente</TableHead>
                  <TableHead className="font-semibold">Piano</TableHead>
                  <TableHead className="font-semibold w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.id} className="hover:bg-muted/20">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {user.avatar_url ? (
                          <img 
                            src={user.avatar_url} 
                            alt="Avatar"
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                            <Users className="w-5 h-5 text-muted-foreground" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-foreground">
                            {user.first_name || user.last_name 
                              ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
                              : 'Utente'}
                          </p>
                          <p className="text-xs text-muted-foreground truncate max-w-[180px]">
                            {user.user_id.slice(0, 8)}...
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {user.subscription ? (
                        <Badge 
                          variant="outline" 
                          className={`${planColors[user.subscription.plan_name] || 'bg-muted'} flex items-center gap-1.5 w-fit`}
                        >
                          <Crown className="w-3 h-3" />
                          {user.subscription.plan_name}
                        </Badge>
                      ) : (
                        <span className="text-sm text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEditSubscription(user)}>
                            {user.subscription ? (
                              <>
                                <Pencil className="w-4 h-4 mr-2" />
                                Modifica piano
                              </>
                            ) : (
                              <>
                                <Plus className="w-4 h-4 mr-2" />
                                Aggiungi abbonamento
                              </>
                            )}
                          </DropdownMenuItem>
                          {user.subscription && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => handleDeleteSubscription(user)}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Rimuovi abbonamento
                              </DropdownMenuItem>
                            </>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => handleDeleteAccount(user)}
                            className="text-destructive focus:text-destructive"
                          >
                            <AlertTriangle className="w-4 h-4 mr-2" />
                            Elimina Account
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedUser?.subscription ? 'Modifica Abbonamento' : 'Aggiungi Abbonamento'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-4">
              Utente: <span className="font-medium text-foreground">
                {selectedUser?.first_name || selectedUser?.last_name 
                  ? `${selectedUser?.first_name || ''} ${selectedUser?.last_name || ''}`.trim()
                  : 'Utente'}
              </span>
            </p>
            <Select value={selectedPlan} onValueChange={setSelectedPlan}>
              <SelectTrigger>
                <SelectValue placeholder="Seleziona piano" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Base">Base - €0,99/mese</SelectItem>
                <SelectItem value="Plus">Plus - €1,99/mese</SelectItem>
                <SelectItem value="Pro">Pro - €2,99/mese</SelectItem>
                <SelectItem value="Ultimate">Ultimate - €3,99/mese</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleSaveSubscription} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Rimuovere abbonamento?</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per rimuovere l'abbonamento {userToDelete?.subscription?.plan_name} di{' '}
              <span className="font-medium">
                {userToDelete?.first_name || userToDelete?.last_name 
                  ? `${userToDelete?.first_name || ''} ${userToDelete?.last_name || ''}`.trim()
                  : 'questo utente'}
              </span>. Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDeleteSubscription}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleting}
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Rimuovi
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Account Confirmation */}
      <AlertDialog open={deleteAccountDialogOpen} onOpenChange={setDeleteAccountDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare account utente?</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per eliminare definitivamente l'account di{' '}
              <span className="font-medium">
                {userToDeleteAccount?.first_name || userToDeleteAccount?.last_name 
                  ? `${userToDeleteAccount?.first_name || ''} ${userToDeleteAccount?.last_name || ''}`.trim()
                  : 'questo utente'}
              </span>. Tutti i dati, abbonamenti e ordini verranno rimossi. Questa azione è irreversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDeleteAccount}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deletingAccount}
            >
              {deletingAccount ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Elimina Account
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminUsersTable;
